/**
  * @class meleChannel
  * @description
  *
  * # Purpose
  * Implements `<mele-channel>` custom web element for playing one channel of <mele-stream>
  */

import meleElement from 'meleElement'

const PROPERTIES = _.extend({}, {
  selected: { description: 'whether this channel is primary', type: 'boolean' },
  ch: { description: 'channel ID', type: 'integer', readonly: true },
  label: { description: 'value of label for channel name', type: 'string', default: 'video.camera' },
  name: { description: 'channel title; symbol # will be replaced by camera channel attribute (this.ch)', type: 'string', default: 'video.camera.#' },
  ets: { description: 'event timestamp - current playback time in miliseconds', type: 'integer', readonly: true },
  vts: { description: 'video timestamp - current video time in miliseconds', type: 'integer', readonly: true },
  videosize: { description: 'video resolution: WIDTHxHEIGHT', type: 'string', readonly: true },
  rendersize: { description: 'video render size: WIDTHxHEIGHT', type: 'string', readonly: true },
}, meleElement._properties)

export default class meleChannel extends meleElement {
  init() {
    const elChannelName = this.appendChildren({ tag: 'mele-channel-name', class: 'indicator' })
    this.elLabel = elChannelName.appendChildren({ tag: 'mele-text', class: 'title' })
    this.elName = elChannelName.appendChildren({ tag: 'mele-text', class: 'subtitle' })
    this.labelChanged(this.label)
    this.nameChanged(this.name)
    this.actors = {}
    super.init()
  }

  labelChanged(label) {
    this.elLabel.label = String(label??'').replace(/#/g, this.ch??'')
  }

  nameChanged(name) {
    this.elName.label = String(name??'').replace(/#/g, this.ch??'')
  }

  async chChanged(ch) {
    this.labelChanged(this.label)
    this.nameChanged(this.name)

    if(ch===1) {
      // this is to ensure that dopplermeta actor is always present for channel 1
      // even before any mmDopplerMeta message is received
      // note: this is not true for other radar types
      // await this.getActor('dopplermeta')
    }
  }

  signature() {
    return this.tagName+'#'+String(this.ch ?? '')
  } // signature

  async getActor(type) {
    let elActor = this.actors[type]
    if(elActor===undefined) {
      elActor = this.ownerDocument.createElement('mele-actor-'+type)
      if(elActor.constructor === HTMLElement) elActor = null // unregistered web element - we store it as null so we don't try to do this again
      this.actors[type] = elActor
      if(elActor) this.appendChild(elActor)
    }
    if(elActor) {
      await elActor._initialized
      elActor.ets = this.ets
      elActor.vts = this.vts
      elActor.videosize = this.videosize
    }
    return elActor
  } // getActor

  async deleteActor(type) {
    const elActor = this.actors[type]
    if(elActor) {
      if(elActor.parentElement===this) this.removeChild(elActor)
      delete this.actors[type]
    }
  } // deleteActor

  async message(msg) {
    const types = this.parentElement.routingHash[msg.action] || [(msg.action.toLowerCase().replace(/^mm/, ''))]
    for(const type of types) {
      const elActor = await this.getActor(type)
      if(elActor && elActor.message) await elActor.message(msg)
    }
  } // message

  async forwardChange(prop, new_value, forward_to_player) {
    if(forward_to_player && this.ch>0 && (this.selected || !this.parentElement.selected)) {
      this.parentElement[prop] = new_value
    }
    for(const type in this.actors) {
      const elActor = await this.getActor(type)
      if(elActor) elActor[prop] = new_value
    }
  }

  async forwardMethod(method, ...args) {
    for(const type in this.actors) {
      const elActor = await this.getActor(type)
      if(elActor && typeof elActor[method]==='function') elActor[method](...args)
    }
  } // forwardMethod

  async etsChanged(new_value) { this.forwardChange('ets', new_value) }
  async vtsChanged(new_value) { this.forwardChange('vts', new_value, true) }
  async videosizeChanged(new_value) { this.forwardChange('videosize', new_value, true) }
  async rendersizeChanged(new_value) { this.forwardChange('rendersize', new_value, true) }
} // class meleChannel

meleChannel._properties = PROPERTIES
meleChannel.observedAttributes = _.keys(PROPERTIES)
window.customElements.define('mele-channel', meleChannel)
